import { z } from 'zod';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Verträge der Systemeinstellungen — Auftrag `PF-modell`, Task 1 des
 * Plans `docs/superpowers/plans/2026-09-29-systemeinstellungen-umsetzung.md`,
 * Spec `docs/superpowers/specs/2026-09-29-systemeinstellungen-design.md`,
 * Abschnitte 2.1 bis 2.3 und 12 (`D-1`, `D-2`, `D-20`, `D-30`, `D-36`).
 *
 * **Jede Aufzählung hier ist dieselbe wie die CHECK-Bedingung in Migration
 * `0255`.** Eine neue Stufe (etwa ein weiterer Voranmeldungszeitraum) ist
 * eine Migration UND eine Zeile hier; `pnpm api:enums:check` hält die
 * Dienstseite gegen diese Liste.
 *
 * **Die Erklärtexte stehen im Vertrag** (`D-20`, Spec 12): Die Installation
 * wird ohne Support ausgeliefert (Entscheid A1), und jede Einstellung muss
 * sich selbst erklären. `EINSTELLUNG_ERKLAERUNG` ist die eine Quelle für die
 * Einstellungsseite und den Einrichtungsassistenten; die Vollständigkeit prüft
 * `E-1712` (Stufe B).
 */
/** Umsatzsteuerstatus — `steuerprofil_ust_status`. */
export const ustStatusSchema = z.enum(['regel', 'kleinunternehmer']);
/** Versteuerungsart — `steuerprofil_versteuerung`. `soll` ist Stufe C (`D-13`). */
export const versteuerungSchema = z.enum(['ist', 'soll']);
/** Gewinnermittlungsart — `steuerprofil_gewinnermittlung` (`D-30`). */
export const gewinnermittlungSchema = z.enum(['bilanz', 'euer']);
/** Voranmeldungszeitraum — `steuerprofil_voranmeldung`. */
export const voranmeldungSchema = z.enum(['monat', 'quartal', 'jahr', 'keine']);
/**
 * **Die Lageregel des Voranmeldungszeitraums, an EINER Stelle** (`E-1856`):
 * welche Zeitraumart ein Steuerstatus zulässt, `null` für „nicht eingestellt".
 *
 * - **Kleinunternehmer:** `keine` oder leer. § 19 Abs. 1 UStG — wer keine Steuer
 *   schuldet, meldet keine an.
 * - **Regelbesteuerung:** `monat`, `quartal`, `jahr` oder leer, nie `keine`.
 *   `jahr` bleibt erlaubt, weil das Finanzamt nach § 18 Abs. 2 Satz 3 UStG von
 *   den Voranmeldungen befreien kann.
 *
 * Die Regel steht dreimal, und genau diese Tabelle ist der Maßstab für die
 * anderen zwei: die Prüfung in `steuerprofilFortschreibenSchema` liest sie, die
 * Auswahl der Oberfläche liest sie über `erlaubteVoranmeldungen()`, und der
 * Trigger `assert_steuerprofil_voranmeldung_lage()` (Migration `0293`, nicht
 * änderbar, SQL kann TypeScript nicht lesen) wird von einem Integrationstest
 * gegen sie gehalten (`apps/api/test/einstellungen/voranmeldung-lage.test.ts`).
 * Wer die Regel ändert, ändert diese Tabelle UND schreibt eine neue Migration;
 * ein Test wird rot, wenn nur eines von beidem geschieht.
 */
export const VORANMELDUNG_JE_STATUS = {
    kleinunternehmer: ['keine', null],
    regel: ['monat', 'quartal', 'jahr', null],
};
/** Ob die Lageregel diesen Zeitraum (oder `null`, leer) unter diesem Status zulässt. */
export function voranmeldungErlaubt(ustStatus, voranmeldung) {
    return VORANMELDUNG_JE_STATUS[ustStatus].includes(voranmeldung);
}
/**
 * Die Werte, die unter einem Status zur Auswahl stehen — ohne „leer", das ist
 * keine wählbare Zeitraumart. Ohne gewählten Status (ein unbekannter Wert)
 * gibt es keine Auswahl.
 */
export function erlaubteVoranmeldungen(ustStatus) {
    const parsed = ustStatusSchema.safeParse(ustStatus);
    if (!parsed.success)
        return [];
    return VORANMELDUNG_JE_STATUS[parsed.data].filter((wert) => wert !== null);
}
/** Herkunft der Installation — `einrichtung_herkunft` (`D-7`: nur Anfangszustand). */
export const einrichtungHerkunftSchema = z.enum(['bestand', 'neu']);
/** Einrichtungszustand — `einrichtung_zustand` (`D-36`: nur vorwärts). */
export const einrichtungZustandSchema = z.enum(['offen', 'abgeschlossen']);
/** Form der Kundennummer des Kundenpanels — `betrieb_einstellung_panel_kundennummer_form`. */
export const panelKundennummerFormSchema = z.enum(['ziffern_normalform', 'zeichenkette']);
/** Der Katalog der Funktionsschalter — `funktion_schalter_katalog` (`D-18`). */
export const funktionSchluesselSchema = z.enum([
    'guthaben',
    'kundenportal',
    'aborechnungen',
    'zahldienste',
    'webhooks',
    'altimport',
    'lastschrift',
]);
/** Quelle eines Vorsystem-Umsatzes — `umsatz_vorsystem_quelle`. */
export const umsatzVorsystemQuelleSchema = z.enum(['einrichtung', 'lexware-office']);
/**
 * Die Rechtsform des Rechnungsstellers — Katalog `rechtsform`, Migration `0256`
 * (Spec 5.1, `D-8`, `D-29`). Stufe A trägt zwei Formen (`einzelunternehmen`,
 * `gmbh_co_kg`), Stufe B die GmbH (`gmbh`, Migration `0274`, `PF-fussgmbh`).
 * Eine neue Form ist eine Katalogzeile, eine Teilvorlage, ein
 * Eintrag in der Positivliste des Renderers UND ein Wert hier — `E-1618` hält
 * alle vier gegeneinander.
 */
export const rechtsformSchema = z.enum(['einzelunternehmen', 'gmbh_co_kg', 'gmbh']);
/** Woher das Logo eines Belegs kommt — `issuer_profile_logo_quelle`, Migration `0256` (`D-16`). */
export const logoQuelleSchema = z.enum(['eingebettet', 'ablage', 'keines']);
/**
 * Bezeichnung, Unterfrage Handelsregister und Erklärtext je Rechtsform — für die
 * Ausstellermaske und den Einrichtungsassistenten (`D-20`). Wörtlich dieselben
 * Werte wie die Katalogzeilen aus `0256`; `E-1618` prüft das gegen die
 * Datenbank.
 */
export const RECHTSFORM_KATALOG = {
    einzelunternehmen: {
        bezeichnung: 'Einzelunternehmen',
        handelsregisterFrage: true,
        erklaertext: 'Sie führen Ihr Unternehmen allein und haften persönlich. Ist Ihre Firma im ' +
            'Handelsregister eingetragen (e. K.), gehören Firma mit Rechtsformzusatz, Ort der ' +
            'Handelsniederlassung, Registergericht und Registernummer auf jeden Geschäftsbrief.',
    },
    gmbh_co_kg: {
        bezeichnung: 'GmbH & Co. KG',
        handelsregisterFrage: false,
        erklaertext: 'Eine Kommanditgesellschaft, deren persönlich haftende Gesellschafterin eine GmbH ist. ' +
            'Auf jeden Geschäftsbrief gehören die Registerangaben der KG sowie Firma, Sitz, ' +
            'Registergericht, Registernummer und Geschäftsführer der Komplementärin.',
    },
    gmbh: {
        bezeichnung: 'GmbH',
        handelsregisterFrage: false,
        erklaertext: 'Eine Kapitalgesellschaft, die mit ihrem Gesellschaftsvermögen haftet. Auf jeden ' +
            'Geschäftsbrief gehören Firma mit Rechtsformzusatz, Sitz, Registergericht, ' +
            'Registernummer und sämtliche Geschäftsführer.',
    },
};
/** Eine Fassung von `steuerprofil`. */
export const steuerprofilSchema = z
    .object({
    id: uuid,
    ustStatus: ustStatusSchema,
    versteuerung: versteuerungSchema,
    gewinnermittlung: gewinnermittlungSchema,
    erloeskontoKleinunternehmer: z
        .string()
        .regex(/^[0-9]{4,6}$/)
        .nullable(),
    voranmeldung: voranmeldungSchema.nullable(),
    dauerfrist: z.boolean(),
    /**
     * Der erste Tag, ab dem dieses System die Umsatzsteuer-Voranmeldung trägt
     * (`steuerprofil.meldewechsel`, Migration `0303`, `D-48` der UStVA-Spec) —
     * ein Monatserster. `null`: ab dem Beginn der Buchführung.
     */
    meldewechsel: isoDate.nullable(),
    finanzamt: z.string().nullable(),
    begruendung: z.string().min(1),
    validFrom: isoDate,
    validTo: isoDate.nullable(),
})
    .strict();
/** Eine Fassung von `freigabeprofil`. */
export const freigabeprofilSchema = z
    .object({
    id: uuid,
    vierAugen: z.boolean(),
    begruendung: z.string().min(1),
    validFrom: isoDate,
    validTo: isoDate.nullable(),
})
    .strict();
/** Die eine Zeile von `einrichtung`. */
export const einrichtungSchema = z
    .object({
    herkunft: einrichtungHerkunftSchema,
    zustand: einrichtungZustandSchema,
    journalbeginn: isoDate.nullable(),
    abgeschlossenAm: z.string().datetime().nullable(),
    abgeschlossenVon: uuid.nullable(),
})
    .strict();
/** Die eine Zeile von `betrieb_einstellung`. */
export const betriebEinstellungSchema = z
    .object({
    zahlungszielTage: z.number().int().min(0).max(365).nullable(),
    nebenbuchBezeichnung: z.string().min(1),
    webkundeHerkunftBezeichnung: z.string().min(1),
    panelKundennummerForm: panelKundennummerFormSchema,
    smtpAbsendername: z.string().nullable(),
    geaendertAm: z.string().datetime(),
    geaendertVon: uuid,
})
    .strict();
/** Eine Zeile von `funktion_schalter`. */
export const funktionSchalterSchema = z
    .object({
    schluessel: funktionSchluesselSchema,
    aktiv: z.boolean(),
    geaendertAm: z.string().datetime(),
    geaendertVon: uuid,
})
    .strict();
/**
 * Was jede Sitzung über die Funktionsschalter erfährt — die Antwort von
 * `GET /api/v1/einstellungen/funktionen` (Auftrag `PF-guthabenschalter`, Spec
 * Systemeinstellungen 8.5, `D-57`).
 *
 * **Nur Schlüssel und Stand**, ohne `geaendertVon`: Die Oberfläche jeder Rolle
 * blendet danach Menüpunkte, Knöpfe und Kacheln aus; wer wann geschaltet hat,
 * steht im Prüfprotokoll und in `GET /api/v1/einstellungen` (`einstellung:manage`).
 * Dieselbe Tabelle `funktion_schalter` wie dort, keine zweite Quelle.
 *
 * **`vierAugen`** (Auftrag `W-vieraugentexte`, `D-31`): Ob die heute geltende
 * Freigaberegel (`freigabeprofil`) eine zweite Person verlangt — `true` auch
 * dann, wenn heute keine Fassung gilt, denn eine fehlende Regel ist keine
 * Erlaubnis (dieselbe Antwort wie `einzelfreigabeErlaubt()` des Servers,
 * verneint). Jede Rolle liest es, damit Freigabetexte und Freigabeknöpfe der
 * Oberfläche der Regel dieser Installation folgen.
 */
export const funktionsstandSchema = z
    .object({
    funktionen: z.array(z.object({ schluessel: funktionSchluesselSchema, aktiv: z.boolean() }).strict()),
    vierAugen: z.boolean(),
})
    .strict();
/**
 * Der Rumpf von `PUT /api/v1/einstellungen/guthaben` — ein- oder ausschalten
 * (`D-57`). Kein Grund, kein Datum: Der Schalter wirkt ab sofort, und wer ihn
 * umlegt, steht im Prüfprotokoll.
 */
export const guthabenSchaltenSchema = z.object({ aktiv: z.boolean() }).strict();
/**
 * Der Schalter des Kundenguthabens samt dem Bestand, den er beim Ausschalten
 * stehen lässt (`GET`/`PUT /api/v1/einstellungen/guthaben`, `D-57`).
 *
 * **`bestand` ist eine Auskunft und keine Bedingung.** Ausschalten verlangt
 * keinen Nullbestand; Konto `1796` und jede Buchung bleiben. `saldo` ist der
 * Saldo des Guthabenkontos am Stichtag (Haben minus Soll, über alle Fassungen
 * der Kontonummer). Die Zahl der Kunden mit Guthaben kennt das Hauptbuch nicht
 * — das Guthabenkonto wird ohne Personenbezug geführt —, sie kommt aus der
 * jüngsten Guthabenabstimmung (Zeilen der Stichtagsdatei, `kundenStichtag`);
 * ohne Abstimmung sind beide `null`.
 */
export const guthabenSchalterSchema = z
    .object({
    aktiv: z.boolean(),
    geaendertAm: z.string().datetime(),
    geaendertVon: uuid,
    bestand: z
        .object({
        stichtag: isoDate,
        saldo: centAmount,
        kunden: z.number().int().nonnegative().nullable(),
        kundenStichtag: isoDate.nullable(),
    })
        .strict(),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/einstellungen`: die heute geltenden
 * Einstellungen.
 *
 * `stichtag` ist der Kalendertag in `Europe/Berlin`, an dem die zwei
 * Fassungen gelesen wurden. `steuerprofil` und `freigabeprofil` sind `null`,
 * wenn für diesen Tag keine Fassung gilt — auf einer frisch eingerichteten
 * Installation nach dem Verwerfen des Grundbestands (`0260`), bis der Kunde
 * seine anlegt.
 */
export const einstellungenSchema = z
    .object({
    stichtag: isoDate,
    steuerprofil: steuerprofilSchema.nullable(),
    freigabeprofil: freigabeprofilSchema.nullable(),
    einrichtung: einrichtungSchema,
    betrieb: betriebEinstellungSchema,
    funktionen: z.array(funktionSchalterSchema),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/einstellungen/marke` — **ohne Anmeldung**
 * erreichbar (`PF-marke`, Spec 7, `D-16`, `E-1657`).
 *
 * **Genau zwei Angaben, und keine dritte.** Die Anmeldeseite braucht wissen,
 * wessen Buchhaltung sie zeigt, und sonst nichts; ein Endpunkt, den jeder
 * Besucher ohne Sitzung abfragen kann, gibt deshalb nur den Einrichtungsstand
 * und den Namen der Firma preis — weder einen Wert der Steuer- oder
 * Freigabefassung noch einen Journalbeginn noch eine Zahl, aus der sich auf
 * Kunden, Umsätze oder Konfiguration schliessen liesse. Der Name steht ohnehin
 * auf jedem Beleg, den die Firma verschickt.
 *
 * **`firmenname` ist zuerst der Name der Lizenz** (`PF-lizenzname`,
 * 2026-10-01, Spec `D-50`, `E-1731`): Ist ein Lizenzschlüssel hinterlegt,
 * dessen Signatur geprüft ist, steht hier sein `kunde` (getrimmt, höchstens
 * 200 Zeichen) — auch vor dem Abschluss der Einrichtung und gleichgültig, ob
 * die Lizenz gerade gültig, abgelaufen oder deaktiviert ist. Die Testlizenz
 * eines Entwicklungsbaus und ein nicht prüfbarer Schlüssel liefern keinen
 * Namen. Das Feld sagt nicht, woher der Name kommt; ob eine Lizenz hinterlegt
 * ist, verrät die Antwort nicht.
 *
 * **Ohne solchen Namen ist `firmenname` `null`, solange die Einrichtung offen
 * ist** — auch dann, wenn der Grundbestand einer Installation noch einen
 * Namen trägt: Vor dem Abschluss ist das der Name des Herstellers, nicht der
 * Firma, die einrichtet. Danach ist es der Name der heute geltenden
 * Ausstellerfassung, und `null`, wenn für heute keine gilt. Die Oberfläche
 * zeigt bei `null` den Produktnamen.
 */
export const markeSchema = z
    .object({
    einrichtung: einrichtungZustandSchema,
    firmenname: z.string().min(1).nullable(),
})
    .strict();
/**
 * Der Rumpf von `journalbeginnVormerken()` — der Beginn der Buchführung,
 * solange die Einrichtung offen ist. Den Endpunkt dafür baut der
 * Einrichtungsassistent (Schritt 4).
 */
export const journalbeginnVormerkenSchema = z
    .object({
    journalbeginn: isoDate.refine(istKalendertag, {
        message: 'Kein Tag des Kalenders (etwa der 30. Februar).',
    }),
})
    .strict();
/**
 * Ist `wert` (Form `JJJJ-MM-TT`, schon von `isoDate` geprüft) ein Tag des
 * Kalenders? `isoDate` prüft nur die Form; ein `2027-02-30` ginge sonst bis
 * in die Datenbank und käme dort als Fehler ohne fachlichen Code zurück.
 * Gerechnet in UTC, damit keine Zeitzone den Tag verschiebt.
 */
/**
 * Ein Monatserster — die Form von `steuerprofil.meldewechsel`
 * (`steuerprofil_meldewechsel_monatserster`, Migration `0303`). Ein
 * Voranmeldungszeitraum beginnt am Ersten eines Monats.
 */
function istMonatserster(wert) {
    return wert.endsWith('-01');
}
function istKalendertag(wert) {
    const [jahr, monat, tag] = wert.split('-').map((teil) => Number.parseInt(teil, 10));
    if (jahr === undefined || monat === undefined || tag === undefined)
        return false;
    const datum = new Date(Date.UTC(jahr, monat - 1, tag));
    return (datum.getUTCFullYear() === jahr &&
        datum.getUTCMonth() === monat - 1 &&
        datum.getUTCDate() === tag);
}
// ===========================================================================
// ZUSTÄNDIGKEITSGRENZEN DER BELEGE — Auftrag `W-zustaendigkeitsgrenzen`,
// Fälle `E-2149` bis `E-2151`. Spec Systemeinstellungen 6.5 (Nachtrag
// 2026-11-03), Spec Lexware-Migration 21.3.
// ===========================================================================
/**
 * Die beiden Zuständigkeitsgrenzen der Belege (`outgoing_scope` und
 * `incoming_scope`, je `journal_from_date`): ab welchem Belegdatum dieses
 * System für Ausgangs- bzw. Eingangsbelege zuständig ist. `null` heisst «noch
 * nichts zuständig» — der leere Zustand sperrt, er erlaubt nicht alles. Die
 * Geldkonten tragen ihre Grenze an ihrem eigenen Konto und stehen hier nicht.
 */
export const zustaendigkeitSchema = z
    .object({
    ausgang: isoDate.nullable(),
    eingang: isoDate.nullable(),
})
    .strict();
const zustaendigkeitTag = isoDate.refine(istKalendertag, {
    message: 'Kein Tag des Kalenders (etwa der 30. Februar).',
});
/**
 * Der Rumpf von `PUT /api/v1/einstellungen/zustaendigkeit`: eine oder beide
 * Grenzen. Was nicht genannt ist, bleibt unberührt. Ein Tag vor dem Beginn der
 * Buchführung weist der Dienst mit `ZUSTAENDIGKEIT_VOR_JOURNALBEGINN` ab; die
 * Datenbank bleibt die letzte Instanz.
 */
export const zustaendigkeitSetzenSchema = z
    .object({
    ausgang: zustaendigkeitTag.optional(),
    eingang: zustaendigkeitTag.optional(),
})
    .strict()
    .refine((eingabe) => eingabe.ausgang !== undefined || eingabe.eingang !== undefined, {
    message: 'Mindestens eine Grenze (ausgang oder eingang) ist anzugeben.',
});
// ===========================================================================
// ERKENNUNG UND BEZEICHNUNGEN — Auftrag `PF-erkennung` (Task 9), Spec 8.4,
// `D-28`, `D-40`, Migration `0262`. Fälle `E-1642` bis `E-1646`.
// ===========================================================================
/** Der Kanal einer Aufladungsform — `erkennung_kennzeichen_kanal`. */
export const erkennungKanalSchema = z.enum(['bank', 'paypal', 'mollie']);
/**
 * Wo die Kundennummer einer Aufladungsform steht — `erkennung_kennzeichen_ziffernfolge`.
 * `dahinter`: `Guthaben Aufladung FS-30880`; `davor`: `41122 Guthaben Aufladung`.
 */
export const ziffernfolgeSchema = z.enum(['dahinter', 'davor']);
/**
 * Ein Textteil einer Aufladungsform: höchstens 60 Zeichen, kein Leerraum am
 * Anfang, kein doppeltes Leerzeichen, kein Steuerzeichen — höchstens EIN
 * Leerzeichen am Ende, das den Teil von der Kundennummer trennt. Dieselbe
 * Form wie die CHECK-Bedingung `erkennung_kennzeichen_textform` in `0262`.
 * **Kein regulärer Ausdruck**: Der Text wird wörtlich verglichen (Spec 8.4).
 */
const formteilSchema = z
    .string()
    .max(60)
    .regex(/^(?:[^\s]+ )*[^\s]*$/u, {
    message: 'Kein Leerraum am Anfang, kein doppeltes Leerzeichen, kein Tabulator.',
})
    .refine((wert) => !/\p{Cc}/u.test(wert), { message: 'Kein Steuerzeichen.' });
/**
 * Eine Aufladungsform: der Text, an dem eine Guthabenaufladung auf einem
 * Kanal erkannt wird. `vorspann` wird ohne Rücksicht auf Gross- und
 * Kleinschreibung verglichen, `kennzeichen` zeichengenau (`E-233`). Vorspann
 * und Kennzeichen zusammen dürfen nicht leer sein — eine leere Form träfe
 * jede Bewegung, die mit einer Zahl beginnt.
 */
export const aufladungsformSchema = z
    .object({
    vorspann: formteilSchema,
    kennzeichen: formteilSchema,
    ziffernfolge: ziffernfolgeSchema,
})
    .strict()
    .refine((form) => `${form.vorspann}${form.kennzeichen}`.trim() !== '', {
    message: 'Vorspann und Kennzeichen zusammen dürfen nicht leer sein.',
});
/** Die Aufladungsformen je Kanal, in der Reihenfolge ihres Vorrangs. Höchstens zehn je Kanal. */
export const aufladungsformenSchema = z
    .object({
    bank: z.array(aufladungsformSchema).max(10),
    paypal: z.array(aufladungsformSchema).max(10),
    mollie: z.array(aufladungsformSchema).max(10),
})
    .strict();
/**
 * Der Rumpf von `PUT /api/v1/einstellungen/erkennung` — der vollständige
 * neue Stand; was nicht genannt ist, gilt danach nicht mehr.
 */
export const erkennungSetzenSchema = z
    .object({
    abbuchungsvereinbarung: z.boolean(),
    aufladung: aufladungsformenSchema,
})
    .strict();
/**
 * Die Antwort von `GET/PUT /api/v1/einstellungen/erkennung`.
 *
 * `rechnungspraefix` und `vorsystemNummernformen` sind Auskunft und werden
 * hier nicht gesetzt: das Kennzeichen des Rechnungskreises steht am
 * Nummernkreis (`number_range.number_prefix`), die Formen der Vorsystemkreise
 * an `document_archive_range.nummernform` (Übernahme aus einem Vorsystem) —
 * eine leere Liste, solange es keinen Vorsystemkreis gibt.
 */
export const erkennungEinstellungSchema = z
    .object({
    abbuchungsvereinbarung: z.boolean(),
    aufladung: aufladungsformenSchema,
    rechnungspraefix: z.string(),
    vorsystemNummernformen: z.array(z.string()),
    geaendertAm: z.string().datetime().nullable(),
    geaendertVon: uuid.nullable(),
})
    .strict();
/** Eine Bezeichnung: nichtleer, höchstens 80 Zeichen, kein Rand-Leerraum, kein Steuerzeichen. */
const bezeichnungSchema = z
    .string()
    .min(1)
    .max(80)
    .refine((wert) => wert === wert.trim() && !/\p{Cc}/u.test(wert), {
    message: 'Kein Leerraum am Rand, kein Steuerzeichen.',
});
/**
 * Der Rumpf von `PUT /api/v1/einstellungen/betrieb` — die Bezeichnungen und
 * die Kundennummernform (`D-40`). Zahlungsziel und Absendername haben noch
 * keinen Leser und kommen mit dem Auftrag, der sie liest.
 */
export const betriebSetzenSchema = z
    .object({
    nebenbuchBezeichnung: bezeichnungSchema,
    webkundeHerkunftBezeichnung: bezeichnungSchema,
    panelKundennummerForm: panelKundennummerFormSchema,
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/einstellungen/bezeichnungen` — die zwei
 * Bezeichnungen, die jede Oberfläche zeigt, lesbar für jede Sitzung.
 */
export const bezeichnungenSchema = z
    .object({
    nebenbuchBezeichnung: z.string().min(1),
    webkundeHerkunftBezeichnung: z.string().min(1),
})
    .strict();
/**
 * Die Bezeichnungen der eigenen Installation — dieselben Werte, die `0255`
 * in `betrieb_einstellung` schreibt. **Nur die Vorgabe, bis die Oberfläche
 * die geladenen Werte hat**; ein Test hält beide gegeneinander
 * (`apps/api/test/einstellungen/bezeichnungen.test.ts`).
 */
export const BEZEICHNUNGEN_BESTAND = {
    nebenbuchBezeichnung: 'FameSystems-Webinterface',
    webkundeHerkunftBezeichnung: 'FameSystems',
};
/** Der Platzhalter im Katalogtext von `1796` (`0262`), ersetzt durch die Nebenbuchbezeichnung. */
export const PLATZHALTER_NEBENBUCH = '{nebenbuch}';
/**
 * Der Rumpf von `POST /api/v1/einstellungen/freigabeprofil` — eine neue
 * Fassung der Freigaberegel (`D-31`, Spec 9, Auftrag `PF-einzelfreigabe`).
 *
 * `validFrom` ist der erste Tag, an dem die Fassung gilt. **Er liegt
 * frühestens heute** (`Europe/Berlin`, `D-4`, `E-1635`): Eine Freigaberegel
 * gilt nie rückwirkend. Geprüft wird das im Dienst gegen den Tag der
 * Datenbank und nicht hier — der Vertrag kennt keine Uhr, und ein Rumpf, der
 * um Mitternacht geschickt wird, darf nicht an der Uhr des Aufrufers hängen.
 *
 * `begruendung` ist Pflicht wie an jeder Fassung (`freigabeprofil_begruendung_nicht_leer`):
 * Wer die zweite Person abschafft, schreibt auf, warum.
 */
export const freigabeprofilFortschreibenSchema = z
    .object({
    vierAugen: z.boolean(),
    validFrom: isoDate.refine(istKalendertag, {
        message: 'Kein Tag des Kalenders (etwa der 30. Februar).',
    }),
    begruendung: z
        .string()
        .max(2000)
        .refine((wert) => wert.trim().length > 0, { message: 'Die Begründung darf nicht leer sein.' }),
})
    .strict();
/**
 * Die zwei Erlöskonten, die ein Kleinunternehmer wählen darf (`D-35`, Spec 3.5):
 * `8200` (Vorgabe) und `8192`. Beide legt Migration `0259` an; welches der
 * Steuerberater wünscht, ist seine Antwort (Frage `DS-1`), und die Wahl bleibt
 * deshalb eine Einstellung.
 */
export const kleinunternehmerErloeskontoSchema = z.enum(['8200', '8192']);
/**
 * Der Rumpf von `POST /api/v1/einstellungen/steuerprofil` — eine neue Fassung
 * der Steuereinstellungen (Spec 2.1 K1, 3.1; Auftrag `PF-assistent`, Fall
 * `E-1665`). Der Schreibweg des Einrichtungsassistenten, Schritt 3, und
 * danach des Änderungsmodus.
 *
 * `validFrom` ist der erste Tag, an dem die Fassung gilt. **Bei offener
 * Einrichtung liegt er spätestens am Beginn der Buchführung** (die Fassung muss
 * den Beginn decken, `einrichtung_abschlusspruefung()`), **danach frühestens
 * heute** (`Europe/Berlin`, `D-4`): Der Status wechselt nie rückwirkend. Beides
 * prüft der Dienst gegen die Datenbank und nicht dieser Vertrag — er kennt
 * weder Uhr noch Einrichtungszustand.
 *
 * **`versteuerung` ist heute nur `ist`.** Die Versteuerung nach vereinbarten
 * Entgelten (`soll`) ist Stufe C (`D-13`); ein Wert, den kein Rechenweg liest,
 * würde still wie `ist` gerechnet — genau die Antwort, die ein Anwender nicht
 * erwartet.
 *
 * **Das Erlöskonto gehört zum Status:** Kleinunternehmer nennt eines der zwei
 * Konten, Regelbesteuerung keines (`steuerprofil_ku_konto`, `0255`).
 *
 * **Der Voranmeldungszeitraum gehört ebenfalls zum Status** (Lageregel,
 * `steuerprofil_voranmeldung_lage`, Migration `0293`, `D-4` der
 * UStVA-Spec): Kleinunternehmer gibt keine Voranmeldung ab (`keine` oder
 * leer), Regelbesteuerung nie `keine` — `jahr` bleibt erlaubt, weil das
 * Finanzamt nach § 18 Abs. 2 Satz 3 UStG befreien kann. Dieselbe Regel steht
 * in der Datenbank; hier steht sie mit einer Meldung in der Sprache des
 * Anwenders, damit niemand die Datenbankmeldung zu sehen bekommt.
 *
 * **`meldewechsel`** ist der erste Tag, ab dem dieses System die Voranmeldung
 * trägt (Migration `0303`, `D-48` der UStVA-Spec), ein Monatserster. **Fehlt
 * die Angabe, übernimmt die neue Fassung den Tag der Fassung davor** — eine
 * Fortschreibung des Zeitraums soll den Tag nicht verlieren. `null` heisst
 * „nicht eingestellt": ab dem Beginn der Buchführung.
 */
export const steuerprofilFortschreibenSchema = z
    .object({
    ustStatus: ustStatusSchema,
    versteuerung: z.literal('ist', {
        errorMap: () => ({
            message: 'Die Versteuerung nach vereinbarten Entgelten (Soll) ist noch nicht verfügbar.',
        }),
    }),
    gewinnermittlung: gewinnermittlungSchema,
    erloeskontoKleinunternehmer: kleinunternehmerErloeskontoSchema.nullable().optional(),
    voranmeldung: voranmeldungSchema.nullable().optional(),
    dauerfrist: z.boolean().optional(),
    meldewechsel: isoDate
        .refine(istKalendertag, { message: 'Kein Tag des Kalenders (etwa der 30. Februar).' })
        .refine(istMonatserster, {
        message: 'Die eigene Voranmeldung beginnt mit einem Monat — bitte den Ersten eines Monats angeben.',
    })
        .nullable()
        .optional(),
    finanzamt: z.string().max(200).nullable().optional(),
    validFrom: isoDate.refine(istKalendertag, {
        message: 'Kein Tag des Kalenders (etwa der 30. Februar).',
    }),
    begruendung: z
        .string()
        .max(2000)
        .refine((wert) => wert.trim().length > 0, { message: 'Die Begründung darf nicht leer sein.' }),
})
    .strict()
    .superRefine((wert, ctx) => {
    const konto = wert.erloeskontoKleinunternehmer ?? null;
    if (wert.ustStatus === 'kleinunternehmer' && konto === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['erloeskontoKleinunternehmer'],
            message: 'Als Kleinunternehmer ist ein Erlöskonto zu wählen (8200 oder 8192).',
        });
    }
    if (wert.ustStatus === 'regel' && konto !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['erloeskontoKleinunternehmer'],
            message: 'Unter Regelbesteuerung gibt es kein Kleinunternehmer-Erlöskonto.',
        });
    }
    const voranmeldung = wert.voranmeldung ?? null;
    if (!voranmeldungErlaubt(wert.ustStatus, voranmeldung)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['voranmeldung'],
            message: wert.ustStatus === 'kleinunternehmer'
                ? 'Als Kleinunternehmer geben Sie keine Umsatzsteuer-Voranmeldung ab. Wählen Sie „keine".'
                : 'Unter Regelbesteuerung geben Sie Umsatzsteuer-Voranmeldungen ab — monatlich, ' +
                    'vierteljährlich oder jährlich, wenn das Finanzamt Sie befreit hat.',
        });
    }
});
/**
 * Die Erklärtexte je Einstellung (`D-20`, Spec 12) — in der Sprache des
 * Anwenders, ohne Verweis auf Code oder Spec. Schlüssel sind die Feldnamen
 * der Antwort; die Funktionsschalter tragen ihren Katalogschlüssel mit dem
 * Vorsatz `funktion.`.
 */
export const EINSTELLUNG_ERKLAERUNG = {
    ustStatus: 'Ob Ihre Rechnungen Umsatzsteuer ausweisen. „Regelbesteuerung": Sie weisen Umsatzsteuer aus ' +
        'und führen sie ab. „Kleinunternehmer" (§ 19 UStG): Sie weisen keine Umsatzsteuer aus, jede ' +
        'Rechnung trägt den Hinweis auf die Steuerbefreiung. Der Status gilt ab einem Datum und wird ' +
        'nie rückwirkend geändert — sprechen Sie einen Wechsel mit Ihrem Steuerberater ab.',
    versteuerung: 'Wann die Umsatzsteuer fällig wird. „Ist": mit dem Zahlungseingang (vereinnahmte Entgelte, ' +
        '§ 20 UStG). „Soll": mit der Rechnung (vereinbarte Entgelte). Dieses System rechnet heute nach ' +
        'Ist.',
    gewinnermittlung: 'Wie Ihr Gewinn ermittelt wird: „Bilanz" (doppelte Buchführung mit Jahresabschluss) oder ' +
        '„Einnahmenüberschussrechnung" (§ 4 Abs. 3 EStG). Das hängt nicht allein an der Rechtsform — ' +
        'eine Handelsgesellschaft bilanziert immer.',
    erloeskontoKleinunternehmer: 'Das Sachkonto, auf das Umsätze als Kleinunternehmer gebucht werden. Nur als Kleinunternehmer ' +
        'nötig; stimmen Sie die Wahl mit Ihrem Steuerberater ab.',
    voranmeldung: 'In welchem Rhythmus Sie Umsatzsteuer-Voranmeldungen abgeben. Als Kleinunternehmer: keine. ' +
        'Unter Regelbesteuerung monatlich oder vierteljährlich; jährlich nur, wenn das Finanzamt Sie ' +
        'befreit hat.',
    meldewechsel: 'Ab welchem Monat dieses System Ihre Umsatzsteuer-Voranmeldung trägt. Für die Monate davor hat ' +
        'Ihr Steuerberater gemeldet; Rechnungen, die vorher ausgestellt und danach bezahlt wurden, ' +
        'stehen auf dem Kennzahlblatt als Übergangsposten gesondert. Ohne Angabe gilt der Beginn der ' +
        'Buchführung.',
    dauerfrist: 'Ob das Finanzamt Ihnen eine Dauerfristverlängerung gewährt hat — die Voranmeldung ist dann ' +
        'einen Monat später fällig. Sie wird auf dem Kennzahlblatt genannt, nicht gerechnet.',
    finanzamt: 'Das für Sie zuständige Finanzamt, wie es auf Ihrem Steuerbescheid steht.',
    vierAugen: 'Ob jede Freigabe (Storno, Gutschrift, Kontierung, Erstattung und weitere) von einer zweiten ' +
        'Person bestätigt werden muss. Ohne diese Regel darf eine Person selbst freigeben; jede solche ' +
        'Freigabe bleibt als Einzelfreigabe sichtbar.',
    journalbeginn: 'Der erste Tag, den dieses System bucht. Alles davor liegt in Ihrem bisherigen System. Er wird ' +
        'bei der Einrichtung festgelegt, lässt sich danach ändern, solange noch nichts gebucht ist, ' +
        'und ist ab der ersten Buchung fest.',
    zustand: 'Ob die Ersteinrichtung abgeschlossen ist. Solange sie offen ist, lassen sich weder Rechnungen ' +
        'ausstellen noch Buchungen schreiben. Eine abgeschlossene Einrichtung wird nicht wieder ' +
        'geöffnet.',
    herkunft: 'Ob diese Installation beim Einspielen der Einstellungen schon Belege trug („Bestand") oder ' +
        'frisch war („neu"). Eine reine Auskunft; sie ändert sich nie.',
    zahlungszielTage: 'Das Zahlungsziel in Tagen, mit dem neue Rechnungen vorbelegt werden.',
    nebenbuchBezeichnung: 'Der Name des Systems, in dem die Guthaben Ihrer Kunden geführt werden — so erscheint es in ' +
        'Kontotexten und Abstimmungen.',
    webkundeHerkunftBezeichnung: 'Wie Kunden bezeichnet werden, die über Ihr Kundenpanel angelegt wurden.',
    panelKundennummerForm: 'Welche Form die Kundennummern Ihres Kundenpanels haben: reine Ziffern ohne führende Null oder ' +
        'eine beliebige Zeichenkette ohne Leerzeichen.',
    smtpAbsendername: 'Der Name, der als Absender Ihrer E-Mails erscheint.',
    aufladung: 'Woran eine Guthabenaufladung Ihrer Kunden im Verwendungszweck erkannt wird — je Kanal (Bank, ' +
        'PayPal, Mollie) ein oder mehrere Texte mit der Kundennummer davor oder dahinter. Der Vorspann ' +
        'wird ohne Rücksicht auf Gross- und Kleinschreibung verglichen, das Kennzeichen zeichengenau. ' +
        'Ohne Form wird auf diesem Kanal keine Aufladung erkannt; die Bewegung bleibt zur Klärung stehen.',
    abbuchungsvereinbarung: 'Ob Zahlungen der Form „Automatische Zahlung - #<Vertrag> (<Kundennummer>)" als Aufladung des ' +
        'Kundenguthabens erkannt werden — die Abbuchungsvereinbarung eines Shops, der den Betrag über ' +
        'PayPal oder Mollie einzieht.',
    rechnungspraefix: 'Das Kennzeichen Ihrer Rechnungsnummern. Eine Zahlung, deren Verwendungszweck mit ihm beginnt, ' +
        'wird als Zahlung auf die Rechnung erkannt. Es steht am Nummernkreis und wird dort festgelegt.',
    vorsystemNummernformen: 'Die Formen der Rechnungsnummern, die aus Ihrem bisherigen System übernommen wurden. Eine Zahlung ' +
        'auf eine solche Rechnung wird an dieser Form erkannt.',
    'funktion.guthaben': 'Kundenguthaben: Aufladungen, Einlösungen, Erstattungen und Korrekturen. Ausgeschaltet verschwindet ' +
        'es aus Menü, Belegen und Dialogen, und kein Weg schreibt mehr Guthaben; der Bestand auf dem ' +
        'Guthabenkonto und jede Buchung bleiben erhalten und lesbar. Einschalten stellt alles wieder her.',
    'funktion.kundenportal': 'Das Kundenportal, in dem Ihre Kunden Belege ansehen und Angebote annehmen.',
    'funktion.aborechnungen': 'Wiederkehrende Rechnungen aus Vorlagen.',
    'funktion.zahldienste': 'Der Abruf von PayPal und Mollie.',
    'funktion.webhooks': 'Meldungen an andere Systeme, etwa über gebuchte Guthabenaufladungen.',
    'funktion.altimport': 'Die Übernahme von Rechnungen aus einem Vorsystem.',
    'funktion.lastschrift': 'Der Einzug per SEPA-Lastschrift.',
};
//# sourceMappingURL=einstellungen.js.map